import { getAllExperiment } from 'shared/experiment'

import { httpClientBanking } from '~/domains/banking/shared/infra/http-client-banking'

import { MultiPromiseHandler } from '../../infra/http/multi-promise-handler'

import { type FeatureFlagsAvailable } from './types'

interface FlagrPayloadResponse {
  resourceId: string
  enabledFeatures: FeatureFlagsAvailable[]
}

type ResourceIdentifier = {
  [key: string]: string
}

type ReduceReturnType = {
  resourceId: string[]
  resourceType: string
}

function createFeatureFlagsPayload(resources: ResourceIdentifier) {
  return Object.entries(resources).reduce<ReduceReturnType[]>(
    (acc, [resourceType, resourceId]) => [...acc, { resourceId: [resourceId], resourceType }],
    []
  )
}

async function fetchFeatureFlags(resources: ResourceIdentifier) {
  const response = await httpClientBanking<FlagrPayloadResponse[]>({
    url: '/flagr/features/resource',
    data: createFeatureFlagsPayload(resources),
    method: 'POST'
  })

  return (response?.data || []).map(flagr => flagr.enabledFeatures).flat()
}

async function fetchExperimentFeatureFlags({ subjectId }: { subjectId?: string }) {
  const experiments = await getAllExperiment(subjectId || '', true, true)
  if (!experiments) {
    return []
  }

  const activeFeatureFlags = Object.entries(experiments).reduce((acc, [name, { value }]) => {
    if (value === 'on') {
      return [...acc, name as FeatureFlagsAvailable]
    }
    return acc
  }, [] as FeatureFlagsAvailable[])

  return activeFeatureFlags
}

interface CombinedFeatureFlags {
  flagr: FeatureFlagsAvailable[]
  amplitude: FeatureFlagsAvailable[]
}

export interface FetchAllHeimdallFlagsProps {
  subjectId?: string
  resources: ResourceIdentifier
}

export async function fetchAllHeimdallFlags({ resources, subjectId }: FetchAllHeimdallFlagsProps) {
  const handler = new MultiPromiseHandler<CombinedFeatureFlags>({ strategy: 'settled' })

  handler.addPromise('flagr', fetchFeatureFlags(resources), [])

  handler.addPromise('amplitude', fetchExperimentFeatureFlags({ subjectId }), [])

  const { amplitude, flagr } = await handler.execute()

  return [...flagr, ...amplitude]
}
